import { useCallback, useEffect } from "react";
import { Shipment, CreateShipment, UpdateShipment } from "../types/models/Shipment.d";
import { UpdateEntityShipment } from "../types/models/Shipment";
import { useGetShipmentListQuery, useInsertShipmentMutation, useUpdateShipmentMutation, useDeleteShipmentMutation, } from "../apis/Shipment.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useShipment(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertShipmentMutation, { 
        isLoading: insertShipmentLoading, 
        isSuccess: insertShipmentIsSuccess,
        isError: insertShipmentIsError,
        error: insertShipmentError,
    }] = useInsertShipmentMutation()
    const [updateShipmentMutation, { 
        isLoading: updateShipmentLoading,
        isSuccess: updateShipmentIsSuccess,
        isError: updateShipmentIsError,
        error: updateShipmentError,
    }] = useUpdateShipmentMutation()
    const [deleteShipmentMutation, { 
        isLoading: deleteShipmentLoading,
        isSuccess: deleteShipmentIsSuccess,
        isError: deleteShipmentIsError,
        error: deleteShipmentError,
    }] = useDeleteShipmentMutation()

    const loading =
        insertShipmentLoading ||
        updateShipmentLoading ||
        deleteShipmentLoading ||
        false

    const insertShipment = useCallback(async (shipment: CreateShipment, redirectPath: string|undefined) => {
        try {
            const data = await insertShipmentMutation(shipment).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertShipmentMutation, navigate])
    
    useEffect(() => {
        if (insertShipmentIsSuccess) {
            openSuccessAlert('Shipment created')
        } else if (insertShipmentIsError) {
            console.log("add error", insertShipmentError)
            openFailAlert(t`Shipment add error: ` + insertShipmentError?.data?.message)
        }
    }, [
        insertShipmentIsSuccess,
        insertShipmentIsError,
        insertShipmentError
    ]);

    const updateShipment = useCallback(async (shipment: UpdateEntityShipment, redirectPath: string|undefined) => {
        try {
            const data = await updateShipmentMutation(shipment).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateShipmentMutation, navigate])
    
    useEffect(() => {
        if (updateShipmentIsSuccess) {
            openSuccessAlert(t`Shipment updated`)
        } else if (updateShipmentIsError) {
            console.log("update error", updateShipmentError)
            openFailAlert(t`Shipment update error: ` + updateShipmentError?.data?.message)
        }
    }, [
        updateShipmentIsSuccess,
        updateShipmentIsError,
        updateShipmentError
    ]);

    const deleteShipment = useCallback((shipment: Shipment, redirectPath: string|undefined) => {
        openDialog(async () => {
            shipment = {
                ...shipment,
            }
            try {
                const { data } = await deleteShipmentMutation(shipment);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteShipmentMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteShipmentIsSuccess) {
            openSuccessAlert(t`Shipment deleted`)
        } else if (deleteShipmentIsError) {
            console.log("delete error", deleteShipmentError)
            openFailAlert(t`Shipment delete error: ` + deleteShipmentError?.data?.message)
        }
    }, [
        deleteShipmentIsSuccess,
        deleteShipmentIsError,
        deleteShipmentError
    ]);

    return {
        loading,
        insertShipment,
        updateShipment,
        deleteShipment
    }
}
